'use client';

import { CurveHealthPanel } from '@slims/ui/organisms';
import { useState } from 'react';
import { useCurveHealth } from '@/hooks/use-curve-health';

/**
 * Tab Healthcheck — la verifica dei dati energetici dei punti di misura del socio.
 *
 * Pura cucitura: la domanda la fa `GET /api/meters/health`, la riparazione
 * `POST /api/meters/health/regenerate`, e la risposta la disegna
 * `CurveHealthPanel`. Sta accanto alle schede dei punti di misura e non dentro
 * una di esse perché la garanzia si legge sul socio intero — un prosumer ha
 * prelievo e immissione, e un orizzonte corto su uno solo dei due è esattamente
 * ciò che sfugge guardandoli a turno.
 *
 * I due pulsanti di rigenerazione stanno qui e non altrove per la stessa
 * ragione: è la scheda che mostra il difetto, e mostrare un difetto senza dare
 * il modo di ripararlo manda l'operatore a cercare una riga di comando che non
 * ha. Dietro chiamano la stessa funzione della catena notturna, con un ambito
 * diverso — vedi `apps/api/src/typical/coverage.ts`.
 */
export function HealthcheckTab({ memberId }: { memberId: string }) {
	const {
		window: win,
		pastDays,
		futureDays,
		meters,
		isLoading,
		error,
		refetch,
		regenerate,
		regenerating,
		note,
		noteFailed,
		series,
		seriesLoading,
		zoom,
		setZoom,
	} = useCurveHealth(memberId);
	const [refreshing, setRefreshing] = useState(false);

	async function refresh() {
		setRefreshing(true);
		try {
			await refetch();
		} finally {
			setRefreshing(false);
		}
	}

	return (
		<CurveHealthPanel
			window={win}
			pastDays={pastDays}
			futureDays={futureDays}
			meters={meters}
			loading={isLoading && meters.length === 0}
			error={error}
			onRefresh={() => void refresh()}
			refreshing={refreshing}
			onRegenerate={(scope) => void regenerate(scope)}
			regenerating={regenerating}
			regenerateNote={note}
			regenerateFailed={noteFailed}
			series={series}
			seriesLoading={seriesLoading}
			zoom={zoom}
			onZoomChange={setZoom}
		/>
	);
}
